import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Layers,
  GripVertical,
  Loader2,
  Settings,
  Cable,
  Activity,
  Zap,
  Shield,
  CheckCircle
} from "lucide-react";

const ICON_OPTIONS = [
  { value: "settings", label: "Settings", icon: Settings },
  { value: "cable", label: "Cable", icon: Cable },
  { value: "activity", label: "Activity", icon: Activity },
  { value: "zap", label: "Zap", icon: Zap },
  { value: "shield", label: "Shield", icon: Shield },
  { value: "check-circle", label: "Check", icon: CheckCircle },
];

const COLOR_OPTIONS = [
  { value: "#3b82f6", label: "Blue" },
  { value: "#f59e0b", label: "Amber" },
  { value: "#10b981", label: "Emerald" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#ef4444", label: "Red" },
  { value: "#06b6d4", label: "Cyan" },
  { value: "#ec4899", label: "Pink" },
  { value: "#84cc16", label: "Lime" },
];

export default function PhasesPage() {
  const { token } = useAuth();
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    color: "#3b82f6",
    icon: "settings",
    order: 1
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchPhases = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/phases`, { headers });
      setPhases(response.data);
    } catch (error) {
      toast.error("Failed to load phases");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchPhases();
  }, [fetchPhases]);

  const handleOpenDialog = (phase = null) => {
    if (phase) {
      setEditingPhase(phase);
      setFormData({
        name: phase.name,
        code: phase.code,
        description: phase.description,
        color: phase.color,
        icon: phase.icon,
        order: phase.order
      });
    } else {
      setEditingPhase(null);
      setFormData({
        name: "",
        code: "",
        description: "",
        color: "#3b82f6",
        icon: "settings",
        order: phases.length + 1
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error("Name and code are required");
      return;
    }

    setSubmitting(true);
    try {
      if (editingPhase) {
        await axios.put(`${API}/phases/${editingPhase.id}`, formData, { headers });
        toast.success("Phase updated");
      } else {
        await axios.post(`${API}/phases`, formData, { headers });
        toast.success("Phase created");
      }
      setShowDialog(false);
      fetchPhases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this phase?")) return;
    
    try {
      await axios.delete(`${API}/phases/${id}`, { headers });
      toast.success("Phase deleted");
      fetchPhases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete phase");
    }
  };

  const getIcon = (iconName) => {
    const iconOption = ICON_OPTIONS.find(i => i.value === iconName);
    if (iconOption) {
      const IconComponent = iconOption.icon;
      return <IconComponent className="w-5 h-5" />;
    }
    return <Settings className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="phases-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Verification Phases</h2>
          <p className="text-muted-foreground">Organize verification steps into logical phases</p>
        </div>
        
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} data-testid="add-phase-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Phase
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingPhase ? "Edit Phase" : "Add New Phase"}</DialogTitle>
              <DialogDescription>
                Define a verification phase to group related steps
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Phase Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Configuration & Physical Setup"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="phase-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Phase Code</Label>
                <Input
                  id="code"
                  placeholder="e.g., configuration"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="font-mono"
                  data-testid="phase-code-input"
                />
                <p className="text-xs text-muted-foreground">Unique identifier (lowercase, no spaces)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe what this phase covers..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="phase-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    min={1}
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 1 })}
                    className="font-mono"
                    data-testid="phase-order-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <div className="flex flex-wrap gap-2">
                    {ICON_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon: option.value })}
                        className={`p-2 rounded-md border transition-colors ${
                          formData.icon === option.value 
                            ? "border-primary bg-primary/10" 
                            : "border-muted hover:border-primary/50"
                        }`}
                        title={option.label}
                      >
                        <option.icon className="w-4 h-4" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: option.value })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.color === option.value 
                          ? "border-foreground scale-110" 
                          : "border-transparent hover:scale-105"
                      }`}
                      style={{ backgroundColor: option.value }}
                      title={option.label}
                    />
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <p className="text-sm font-medium mb-2 text-muted-foreground">Preview</p>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: formData.color }}
                  >
                    {getIcon(formData.icon)}
                  </div>
                  <div>
                    <p className="font-medium">{formData.name || "Phase Name"}</p>
                    <p className="text-sm text-muted-foreground font-mono">{formData.code || "phase_code"}</p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="save-phase-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingPhase ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Phases List */}
      {phases.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Layers className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No phases configured yet</p>
              <p className="text-sm text-muted-foreground">Add phases to organize your verification steps</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {phases.map((phase) => (
            <Card 
              key={phase.id} 
              className="card-hover relative overflow-hidden"
              data-testid={`phase-card-${phase.code}`}
            >
              <div 
                className="absolute top-0 left-0 w-1 h-full"
                style={{ backgroundColor: phase.color }}
              />
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: phase.color }}
                    >
                      {getIcon(phase.icon)}
                    </div>
                    <div>
                      <CardTitle className="text-base">{phase.name}</CardTitle>
                      <CardDescription className="font-mono text-xs">{phase.code}</CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline" className="font-mono">#{phase.order}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {phase.description || "No description"}
                </p>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(phase)}
                    data-testid={`edit-phase-${phase.code}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(phase.id)}
                    data-testid={`delete-phase-${phase.code}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
